import { Head, Link, router } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, ChevronRight, Play, RotateCcw } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useFormat } from "@/lib/format"
import type { ExamAttemptSummary, ExamSummary, UnfinishedAttempt } from "@/types/exams"

export default function PracticeExamShow({
  exam,
  halves,
  cases,
  unfinished,
  recent_attempts,
}: {
  exam: ExamSummary
  halves: { case_count: number; question_count: number }[]
  cases: { id: number; title: string; presenting_concern: string | null; question_count: number }[]
  unfinished: UnfinishedAttempt | null
  recent_attempts: ExamAttemptSummary[]
}) {
  const intl = useIntl()
  const format = useFormat()
  const start = (data: Record<string, number> = {}) => router.post(`/practice_exams/${exam.id}/exam_attempts`, data)
  const description = intl.formatMessage(
    { id: "exams.show_meta" },
    {
      title: exam.title,
      cases: format.caseCount(exam.case_count),
      questions: format.questionCount(exam.question_count),
      duration: format.duration(exam.time_limit_minutes * 60),
    },
  )

  return (
    <>
      <Head title={exam.title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={exam.title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <Link href="/practice_exams" className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {intl.formatMessage({ id: "exams.title" })}
        </Link>
        <PageHeader
          title={exam.title}
          description={
            <>
              {exam.exam_version_name} · {format.caseCount(exam.case_count)} · {format.questionCount(exam.question_count)}
              {exam.best_score != null ? ` · ${intl.formatMessage({ id: "quizzes.best_score" }, { score: exam.best_score })}` : ""}
            </>
          }
          actions={
            unfinished ? (
              <>
                <Button type="button" variant="secondary" onClick={() => start()}>
                  <RotateCcw className="h-4 w-4" /> {intl.formatMessage({ id: "exams.start_over" })}
                </Button>
                <Button asChild>
                  <Link href={`/exam_attempts/${unfinished.id}`}>
                    <Play className="h-4 w-4" /> {intl.formatMessage({ id: "exams.resume_exam" })}
                  </Link>
                </Button>
              </>
            ) : (
              <Button type="button" onClick={() => start()}>
                <Play className="h-4 w-4" /> {intl.formatMessage({ id: "exams.start_full" })}
              </Button>
            )
          }
        />

        {unfinished && (
          <div className="callout callout-signal mt-6">
            <p>
              {intl.formatMessage({ id: "exams.in_progress_note" }, {
                answered: unfinished.answered,
                questions: format.questionCount(unfinished.question_count),
                remaining: format.duration(unfinished.time_remaining_seconds),
                onBreak: String(unfinished.on_break),
              })}
            </p>
          </div>
        )}

        <section className="mt-8">
          <h2>{intl.formatMessage({ id: "exams.how_title" })}</h2>
          <ul className="mt-2">
            <li>
              {halves.length === 2
                ? intl.formatMessage(
                    { id: "exams.rule_time_two" },
                    {
                      duration: format.duration(exam.time_limit_minutes * 60),
                      cases: format.caseCount(exam.case_count),
                      first: format.caseCount(halves[0].case_count),
                      second: format.caseCount(halves[1].case_count),
                    },
                  )
                : intl.formatMessage(
                    { id: "exams.rule_time_one" },
                    { duration: format.duration(exam.time_limit_minutes * 60), cases: format.caseCount(exam.case_count) },
                  )}
            </li>
            <li>{intl.formatMessage({ id: "exams.rule_intake" })}</li>
            <li>{intl.formatMessage({ id: "exams.rule_move" })}</li>
            {halves.length === 2 && <li>{intl.formatMessage({ id: "exams.rule_break" })}</li>}
            <li>{intl.formatMessage({ id: "exams.rule_feedback" })}</li>
            <li>{intl.formatMessage({ id: "exams.rule_save" })}</li>
          </ul>
          {exam.instructions && <p className="mt-3 whitespace-pre-wrap">{exam.instructions}</p>}
        </section>

        <section className="mt-10">
          <h2>{intl.formatMessage({ id: "exams.practice_one" })}</h2>
          <p className="mt-1">{intl.formatMessage({ id: "exams.practice_one_hint" })}</p>
          <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-page">
            {cases.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => start({ case_study_id: c.id })}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left hover:bg-surface"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink-display">{c.title}</div>
                    <div className="truncate text-xs text-ink-muted">
                      {format.questionCount(c.question_count)}
                      {c.presenting_concern ? ` · ${c.presenting_concern}` : ""}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-10">
          <h2>{intl.formatMessage({ id: "exams.attempts_title" })}</h2>
          {recent_attempts.length === 0 ? (
            <p className="mt-2">{intl.formatMessage({ id: "exams.no_attempts" })}</p>
          ) : (
            <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-page">
              {recent_attempts.map((a) => (
                <li key={a.id}>
                  <Link
                    href={`/exam_attempts/${a.id}/results`}
                    className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-ink-display">{a.score != null && format.number(a.score / 100, { style: "percent" })}</div>
                      <div className="truncate text-xs text-ink-muted">
                        {a.finished_on && format.date(a.finished_on, { month: "short", day: "numeric", year: "numeric" })} · {format.questionCount(a.question_count)}
                        {a.mode === "full_exam" ? ` · ${intl.formatMessage({ id: "exams.time_used" }, { duration: format.duration(a.time_used_seconds) })}` : ""}
                      </div>
                    </div>
                    <Badge tone={a.mode === "full_exam" ? "accent" : "muted"}>
                      {a.mode === "full_exam" ? intl.formatMessage({ id: "exams.full_exam" }) : intl.formatMessage({ id: "exams.case_badge" }, { title: a.case_title })}
                    </Badge>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </AppShell>
    </>
  )
}
